import { useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// DELIBERATE ARCHITECTURAL EXCEPTION - please read before "fixing" this.
//
// This is the only hook in the app that performs network I/O directly. It sits
// outside Redux, outside the saga layer and outside src/request/ on purpose.
// The rule it breaks is stated in CLAUDE.md ("side effects live in sagas, never
// in components"), and the exception is recorded there too.
//
// Why it does not use the shared axios instance (src/request/index.js):
//   * That instance carries withCredentials and an Authorization header set by
//     addHeaderToken(). This is a public static file on a CDN; it must receive
//     neither, or session JWTs end up in CloudFront access logs.
//   * requestStatusMiddleware turns any 401 outside three login endpoints into
//     dispatch(logoutUser({})). A 60s background poll on that path would make a
//     CDN misconfiguration a rolling mass-logout.
//
// Why it does not live in Redux:
//   * The fail-open guarantee below is the single most important property here.
//     As one function it sits three lines from its own catch and is covered
//     directly by its tests. Through Redux it would be spread across an action,
//     a reducer and a selector, with the error path also needing to resolve to
//     false.
//   * loadingMiddleware matches FETCH/SUCCESS/ERROR anywhere in an action type,
//     so conventional action names would drive the global spinner every 60s.
//   * Nothing is shared: one boolean, read by App.js, written by nobody.
//
// Why setInterval rather than the workers/ polling infrastructure:
//   * createWorker() spawns a Web Worker per name into a module map that is
//     never cleared - nothing calls terminate() and no blob URL is revoked.
//     (See the feature/fix-worker-teardown-leak branch.) This hook cleans up
//     after itself instead.
//   * A Worker exists to move work off the main thread. This is one ~30 byte
//     fetch a minute; there is nothing to offload.
//   * That infrastructure ticks a 1s countdown to fire on a longer interval,
//     so a 60s poll would mean a timer running 60x more often than the work.
//
// Operational notes live in scripts/maintenance/README.md.
// ---------------------------------------------------------------------------

// The flag is a static object published next to the app in the same S3 bucket,
// so it is served by the same CloudFront distribution as the bundle that reads
// it. That coupling is deliberate: the flag can never be unreachable while the
// app itself is reachable.
export const MAINTENANCE_FLAG_URL = '/maintenance.json';
export const MAINTENANCE_POLL_INTERVAL_MS = 60000;
export const MAINTENANCE_FETCH_TIMEOUT_MS = 10000;
// Width of the cache-busting bucket. See buildFlagUrl.
export const MAINTENANCE_CACHE_BUCKET_MS = 30000;
// Reloads after a window are spread across this window so every tab does not
// hit a just-restarted backend at once.
export const MAINTENANCE_RELOAD_JITTER_MS = 30000;

// There can be TWO caches in front of this object, and they behave differently.
// Measured, not assumed:
//
//   * Cloudflare fronts CloudFront on 9 of the 10 hosts (the exception is
//     gs-app-uat, which answers `server: AmazonS3`). Its cache key DOES
//     include the query string, so a changing one misses it. No request header
//     dislodges it - `no-store` and `no-cache` both still return a HIT.
//   * CloudFront's cache key does NOT include the query string on these
//     distributions: two different `?t=` values return the same entry, with an
//     identical `age`. So the query string cannot bust CloudFront.
//
// The two mechanisms are therefore complementary, each covering the layer the
// other cannot reach: the rolling bucket below handles Cloudflare, and the
// CloudFront invalidation issued on every write (toggle.sh and the deploy seed
// step) handles CloudFront. Neither is redundant.
//
// In steady state CloudFront honours the object's own max-age=30. The case the
// invalidation exists for is the first activation, when CloudFront may still
// be holding a cached fallback from before the object existed.
export function buildFlagUrl(url = MAINTENANCE_FLAG_URL) {
  const bucket = Math.floor(Date.now() / MAINTENANCE_CACHE_BUCKET_MS);
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}t=${bucket}`;
}

// Deliberately plain fetch rather than the shared axios instance: that instance
// carries the session Authorization header and withCredentials, neither of
// which should ever be sent to the CDN for a public static file.
//
// Returns { maintenance, ok }. `ok` means the CDN actually answered with a
// readable JSON body - it separates "we asked and it said false" from "we could
// not tell". Display always fails open on both, but anything with consequences
// (see the reload below) must only act on a definite answer.
export async function readMaintenanceFlag(url = MAINTENANCE_FLAG_URL) {
  let timeoutId = null;

  try {
    // A hung request would otherwise sit in flight across poll ticks. There is
    // no AbortController guard on purpose: on a browser old enough to lack it,
    // the throw lands in the catch below and reads as "not in maintenance",
    // which is the same fail-open answer we want anyway.
    const controller = new AbortController();
    timeoutId = setTimeout(
      () => controller.abort(),
      MAINTENANCE_FETCH_TIMEOUT_MS,
    );

    const response = await fetch(buildFlagUrl(url), {
      cache: 'no-store',
      // 'omit', not the default. fetch defaults to credentials: 'same-origin',
      // and the flag IS same-origin with the app - so cookies set on the app
      // origin (or on a parent domain) would ride along on every poll and land
      // in CloudFront access logs. Nothing about this request is authenticated.
      credentials: 'omit',
      signal: controller.signal,
    });

    if (!response.ok) {
      return { maintenance: false, ok: false };
    }

    const payload = await response.json();

    // Strict equality only. A malformed, empty or unexpected payload must read
    // as "not in maintenance".
    return { maintenance: payload?.maintenance === true, ok: true };
  } catch (error) {
    // Fail open. A CDN blip, a DNS hiccup, a timeout, an SPA-fallback HTML body
    // or a missing file must never put the portal into maintenance mode - and
    // must never be mistaken for a window having ended.
    return { maintenance: false, ok: false };
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  }
}

export async function fetchMaintenanceFlag(url = MAINTENANCE_FLAG_URL) {
  const { maintenance } = await readMaintenanceFlag(url);

  return maintenance;
}

export default function useMaintenanceFlag() {
  const [maintenance, setMaintenance] = useState(false);
  const isMounted = useRef(true);
  // Only ever set from a definite answer, so a failed read cannot look like a
  // window starting or ending.
  const wasInMaintenance = useRef(false);
  const reloadTimer = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    let timer = null;
    let generation = 0;

    const read = async () => {
      generation += 1;
      const readGeneration = generation;

      const { maintenance: isDown, ok } = await readMaintenanceFlag();

      // A visibilitychange read can start while an interval read is still in
      // flight. Without this guard the slower response wins, so a stale answer
      // fetched before a toggle could clobber a fresh one and leave the tab
      // showing the wrong state until the next tick.
      if (!isMounted.current || readGeneration !== generation) {
        return;
      }

      // A read that failed tells us nothing, so hold the last confirmed
      // answer rather than reporting false. Reporting false mid-window would
      // flip every tab out of the maintenance page and back into the live app,
      // re-running startup against the backend that is down, then flip back on
      // the next good poll.
      //
      // Fail-open is preserved by the initial state, not by this path: we
      // start at false and only ever move to true on a confirmed read, so a
      // CDN that is broken from the outset can never put the portal into
      // maintenance. If reads keep failing after a confirmed window, staying
      // on the maintenance page is the right answer anyway - the flag shares
      // an origin with the bundle, so persistent read failures mean the app
      // itself is not reliably reachable either.
      if (!ok) {
        return;
      }

      // A window has ended. Reload rather than re-running the parts of startup
      // that were skipped: a tab that sat through a window has no user, no
      // axios auth header and no account, and re-running those effects
      // piecemeal re-dispatches fetchUserAccounts with setDefaultAccount, which
      // would silently move a multi-account user off the account they had
      // selected. A reload re-initialises everything in one predictable step.
      //
      // Because this is the ONLY path that clears a confirmed window, every
      // true -> false transition is followed by a reload. Effects in
      // useInitHook that skip themselves during maintenance can therefore rely
      // on being re-run, without carrying isMaintenanceMode as a dependency.
      if (wasInMaintenance.current && !isDown) {
        if (reloadTimer.current === null) {
          // Spread the herd. Every tab that sat through the window would
          // otherwise reload inside one poll interval of every other, putting
          // a full bundle fetch plus the whole startup dispatch set per tab
          // onto a backend that has only just come back up.
          reloadTimer.current = setTimeout(
            () => window.location.reload(),
            Math.random() * MAINTENANCE_RELOAD_JITTER_MS,
          );
        }
        return;
      }

      // A pending reload from an earlier true -> false transition must not
      // survive a genuine return to maintenance in the meantime. In practice
      // this only has a chance to matter via an out-of-band read - a
      // visibilitychange refocus - since the regular 60s poll interval is
      // longer than the reload's own 0-30s jitter and so can never observe a
      // re-enable before a stale reload would already have fired.
      if (isDown && reloadTimer.current !== null) {
        clearTimeout(reloadTimer.current);
        reloadTimer.current = null;
      }

      wasInMaintenance.current = isDown;
      setMaintenance(isDown);
    };

    const stopPolling = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    // Poll so a window opened before the flag was flipped still picks it up.
    const startPolling = () => {
      stopPolling();
      timer = setInterval(read, MAINTENANCE_POLL_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Nobody is looking. Stop asking the CDN until they come back.
        stopPolling();
        return;
      }

      // Re-read straight away instead of waiting out the interval, so a tab
      // that sat in the background shows the current state on focus rather
      // than up to a minute of stale state.
      read();
      startPolling();
    };

    read();

    if (!document.hidden) {
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted.current = false;
      stopPolling();
      if (reloadTimer.current !== null) {
        clearTimeout(reloadTimer.current);
        reloadTimer.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return maintenance;
}
